import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { getCurrentUser, fetchUserAttributes, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useQueryClient } from '@tanstack/react-query';

export interface AppUser {
  userId: string;
  username: string;
  email: string;
}

export interface AuthState {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

interface AuthProviderProps {
  readonly children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const fetchUser = useCallback(async () => {
    try {
      const [currentUser, attributes] = await Promise.all([
        getCurrentUser(),
        fetchUserAttributes(),
      ]);
      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: attributes.email ?? currentUser.username,
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUser();
    // The mount-time fetch above only sees the session that existed at
    // page load. Auth transitions after that (signIn() on the sign-in
    // page, sign-out from another tab) arrive as Hub events — without
    // this listener the SPA keeps rendering the stale session until a
    // full reload.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') void fetchUser();
      if (payload.event === 'signedOut') setUser(null);
    });
    return unsubscribe;
  }, [fetchUser]);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    // The next account on this browser must not see this user's cached
    // queries (settings, plan usage, monitors, scores).
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  const value: AuthState = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    signOut,
    refresh: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
